import React, { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import supabase from "../supabaseClient";
import UserAvatar from "../Assets/user-avatar.svg"
import Avatar1 from "../Assets/Avatars/avatar1.png";
import Avatar2 from "../Assets/Avatars/avatar2.png";
import Avatar3 from "../Assets/Avatars/avatar3.png";
import Avatar4 from "../Assets/Avatars/avatar4.png";
import Avatar5 from "../Assets/Avatars/avatar5.png";
import Avatar6 from "../Assets/Avatars/avatar6.png";
import Menu from "../Assets/menu.svg"
import "./CSS/MobileHomeHeader.css";
import { FaUserEdit, FaSignOutAlt, FaArrowLeft, FaPlus, FaUserShield } from "react-icons/fa";
import useBackSwipe from "../hooks/useBackSwipe";
import { animateBackAndRun } from "../hooks/useBackSwipe";
import {
  clearPasskeyForUser,
  enrollLocalPasskey,
  getLastPasskeyError,
  getPasskeySupportIssue,
  isPasskeyEnabledForUser,
  isPasskeySupported,
  setPasskeyEnabledForUser,
  verifyLocalPasskey,
} from "../auth/passkey";

const MobileHomeHeader = ({ profile, setShowNameModal, setProfile }) => {
  const navigate = useNavigate();
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [showAvatarPicker, setShowAvatarPicker] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [passkeyEnabled, setPasskeyEnabled] = useState(false);
  const settingsViewRef = useRef(null);
  const settingsUnderlayRef = useRef(null);
  const settingsDimmerRef = useRef(null);
  const avatarOptions = [UserAvatar, Avatar1, Avatar2, Avatar3, Avatar4, Avatar5, Avatar6];
  const fileRef = useRef(null);
  
  const userName = profile?.name || "Guest User";
  const userFirstName = (profile?.name || "").split(' ')[0];
  const userEmail = profile?.email || "No Email Found";

  const isPreservedPath = (url) =>
  url.startsWith("/static") || url.startsWith("data:") || url.includes("Assets/");

  const avatarSrc = profile?.avatar_url
    ? isPreservedPath(profile.avatar_url)
        ? profile.avatar_url
        : `${process.env.REACT_APP_API_BASE_URL}/api/image/${encodeURIComponent(profile.avatar_url)}`
    : UserAvatar;

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    navigate("/login");
  };

  const handleTogglePasskey = async () => {
    if (!profile?.id) return;
    if (!isPasskeySupported()) {
      alert("Face ID/Touch ID unlock is only available in the mobile app.");
      return;
    }
    const supportIssue = await getPasskeySupportIssue();
    if (supportIssue) {
      alert(supportIssue);
      return;
    }

    if (passkeyEnabled) {
      const ok = await verifyLocalPasskey({ userId: profile.id });
      if (!ok) return;
      clearPasskeyForUser(profile.id);
      setPasskeyEnabled(false);
      return;
    }

    const enrolled = await enrollLocalPasskey({
      userId: profile.id,
      userName: profile?.email || profile?.name || profile.id,
    });
    if (enrolled) {
      setPasskeyEnabled(true);
      setPasskeyEnabledForUser(profile.id, true);
    } else {
      alert(getLastPasskeyError() || "Could not enable Face ID/Touch ID unlock.");
    }
  };

  const handleSwitchToAdmin = () => {
    setSettingsOpen(false);
    navigate("/admin");
  };

  useEffect(() => {
    if (!settingsOpen) return;
    settingsUnderlayRef.current = document.querySelector(".mobile-underlay");
    settingsDimmerRef.current = document.querySelector(".mobile-underlay-dim");
  }, [settingsOpen]);

  useEffect(() => {
    if (!profile?.id) return;
    setPasskeyEnabled(isPasskeyEnabledForUser(profile.id));
  }, [profile?.id]);

  useBackSwipe(
    () => setSettingsOpen(false),
    {
      enabled: settingsOpen,
      target: settingsViewRef,
      underlay: settingsUnderlayRef,
      dimmer: settingsDimmerRef,
    }
  );

  return (
    <>
    {settingsOpen && createPortal(
      <div className="settings-page fade-in-settings" ref={settingsViewRef}>
        <button
          className="back-button-settings"
          onClick={() =>
            animateBackAndRun({
              target: settingsViewRef,
              underlay: settingsUnderlayRef,
              dimmer: settingsDimmerRef,
              onBack: () => setSettingsOpen(false),
            })
          }
        >
          <FaArrowLeft className="back-icon" />
          Back
        </button>
      <div className="settings-header">
          <h2 className="settings-title">Settings</h2>
        </div>

      <div className="profile-avatar-section">
        <div className="avatar-circle">
          <img
            src={avatarSrc}
            alt="User Avatar"
            className="settings-avatar"
          />
        </div>
          <button className="edit-avatar-btn" onClick={() => setShowAvatarPicker(true)}>
            Edit Avatar
          </button>

        {showAvatarPicker && (
          <div className="avatar-picker">
            <h4>Choose Your Avatar</h4>
            {uploading && (
              <div className="avatar-upload-overlay">
                <div className="spinner" />
              </div>
            )}
            <div className="avatar-grid">
              {avatarOptions.map((avatar, idx) => (
                <img
                  key={idx}
                  src={avatar}
                  alt={`Avatar ${idx + 1}`}
                  className="avatar-choice"
                  onClick={async () => {
                    await supabase
                      .from("profiles")
                      .update({ avatar_url: avatar })
                      .eq("id", profile.id);
                    setShowAvatarPicker(false);
                    setProfile({ ...profile, avatar_url: avatar });
                  }}
                />
              ))}
              <div className="avatar-choice upload-avatar-tile">
                <button
                  className="avatar-upload-btn"
                  onClick={() => fileRef.current?.click()}
                  aria-label="Upload Avatar"
                >
                  <FaPlus className="camera-icon" />
                </button>
                <input
                  ref={fileRef}
                  type="file"
                  accept="image/*"
                  style={{ display: "none" }}
                  onChange={async (e) => {
                    const file = e.target.files[0];
                    if (!file) return;
                    setUploading(true);
                    const formData = new FormData();
                    formData.append("avatar", file);
                    formData.append("userId", profile.id);

                    try {
                      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/upload-avatar`, {
                        method: "POST",
                        body: formData,
                      });
                      const { objectKey } = await res.json();
                      setProfile({ ...profile, avatar_url: objectKey });
                      setShowAvatarPicker(false);
                    } catch (err) {
                      console.error("Avatar upload failed", err);
                    } finally {
                      setUploading(false);
                    }
                  }}
                />
              </div>
            </div>
          </div>
        )}
          <div className="profile-details">
          <div className="user-name">{userName}</div>
          <div className="user-email">{userEmail}</div>
        </div>
        </div>

        <div className="settings-section">         
        <div className="settings-item" onClick={() => { setShowNameModal(true); setSettingsOpen(false); }}>
        <div className="item-icon"><FaUserEdit /></div>
        <div className="item-text">Edit Profile</div>
        </div>

        <div className="settings-item" onClick={handleTogglePasskey}>
        <div className="item-icon"><FaUserShield /></div>
        <div className="item-text">
          {passkeyEnabled ? "Disable Face ID / Touch ID" : "Enable Face ID / Touch ID"}
        </div>
        </div>

        <div className="settings-item" onClick={handleSignOut}>
        <div className="item-icon"><FaSignOutAlt /></div>
        <div className="item-text">Sign Out</div>
      </div>
      {profile?.is_admin && (
        <div className="settings-item" onClick={handleSwitchToAdmin}>
        <div className="item-icon"><FaUserShield /></div>
        <div className="item-text">Switch to Admin</div>
        </div>
      )}
        </div>
      </div>,
      document.body
    )}
    <div className="simple-header" onClick={() => setSettingsOpen(true)}>
      <div className="user-greeting">
        <div className="avatar-container">
          <img
            src={avatarSrc}
            alt="User Avatar"
            className="greeting-avatar"
          />
        </div>
        <div className="greeting-text">
          <h2 className="greeting-heading">Hey {userFirstName}!</h2>
          <p className="greeting-subtext">Welcome to PaiCoach</p>
        </div>
      </div>
      <button className="menu-button">
      <img src={Menu} alt="Menu" className="menu-icon" />
      </button>
      </div>
      </>
  );
};

export default MobileHomeHeader;
